import { UserRoles } from '@/models/company';
import { DashboardIcon } from '@/icons/DashboardIcon';
import { PendingApprovalsIcon } from '@/icons/PendingApprovalsIcon';
import { EmployeesIcon } from '@/icons/EmployeesIcon';
import { TransactionsIcon } from '@/icons/TransactionsIcon';
import { ReconciliationIcon } from '@/icons/ReconciliationIcon';
import { CompanyIcon } from '@/icons/CompanyIcon';
import { CompanyAccountsIcon } from '@/icons/CompanyAccountsIcon';
import { EmailsIcon } from '@/icons/EmailsIcon';
import { SettingsIcon } from '@/icons/SettingsIcon';
import { SubscriptionIcon } from '@/icons/SubscriptionIcon';
import { LogoutIcon } from '@/icons/LogoutIcon';
import { useEffect, useMemo, useState } from 'react';
import { useAdminCompanies, useAppDispatch } from '@/lib/hooks';
import { getAdminCompanies } from '@/lib/slices/paymentsSlice';
import { FETCH_STATE } from '@/lib/slices/types';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { useNavigate } from 'react-router-dom';
import { getCompanyIdFromLocalStorage } from '@/services/companyService';
import { useUser } from '@/contexts/user/UserContext';
import { TipsIcon } from '@/icons/TipsIcon';
import { InstaSignIcon } from '@/icons/InstaSignIcon';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';
//import DashboardIcon

export type MenuLink = {
    label: string;
    icon: JSX.Element;
    url: string;
    requiredRole?: UserRoles[];
};

type MenuTree = Array<{
    label?: string;
    role: UserRoles[];
    items: MenuLink[];
}>;

const sideBarIconFontSize = { fontSize: '18px' };

export const useMenuTree = () => {
    const dispatch = useAppDispatch();

    const { user } = useUser();

    const { status, companies } = useAdminCompanies();

    const { data: currentCompany } = useCurrentCompanyDetails();

    useEffect(() => {
        dispatch(getAdminCompanies());
    }, []);

    useEffect(() => {
        dispatch(setLoader(status == FETCH_STATE.LOADING));
    }, [status]);

    const navigate = useNavigate();

    const [selectedCompanyName, setSelectedCompanyName] = useState<
        string | null
    >(null);

    useEffect(() => {
        const selectedCompanyId = getCompanyIdFromLocalStorage();

        if (companies?.length == 1 && !selectedCompanyId) {
            navigate(`/?changeCompanyId=${companies[0].companyId}`);
            return;
        }

        const selectedCompany = companies?.find(
            (c) => c.companyId == selectedCompanyId
        );

        if (selectedCompany) {
            setSelectedCompanyName(selectedCompany.name);
        }
    }, [companies]);

    const getAdminLinks = () => {
        if (user.role == UserRoles.SuperAdmin) {
            return {
                label: 'Insta Admin',
                role: [UserRoles.SuperAdmin],
                items: [
                    {
                        url: '/companies/v2',
                        label: 'Companies',
                        icon: <CompanyIcon style={sideBarIconFontSize} />
                    },
                    {
                        url: '/companyAccounts',
                        label: 'Company accounts',
                        icon: (
                            <CompanyAccountsIcon style={sideBarIconFontSize} />
                        )
                    },
                    {
                        url: '/all-employees/page/0',
                        label: 'All employees',
                        icon: <EmployeesIcon style={sideBarIconFontSize} />
                    },
                    // {
                    //     url: '/instaconfig/emails',
                    //     label: 'InstaConfig',
                    //     icon: <EmailsIcon style={sideBarIconFontSize} />
                    // },
                    {
                        url: '/transaction-monitoring/page/0',
                        label: 'Transaction Monitoring',
                        icon: <SettingsIcon style={sideBarIconFontSize} />
                    }
                ]
            };
        } else {
            return {
                label: 'Group',
                role: [
                    UserRoles.Admin,
                    UserRoles.Manager,
                    UserRoles.SuperAdmin
                ],
                items: [
                    {
                        url: '/companies/v2',
                        label: 'Companies',
                        icon: <DashboardIcon style={sideBarIconFontSize} />
                    }
                ]
            };
        }
    };

    let commonLinks = [
        {
            label: selectedCompanyName ?? 'General',
            role: [UserRoles.Admin, UserRoles.Manager, UserRoles.SuperAdmin],
            items: [
                {
                    url: '/',
                    label: 'Dashboard',
                    icon: <DashboardIcon style={sideBarIconFontSize} />
                },
                {
                    url: '/payments',
                    label: 'Pending transactions',
                    icon: <PendingApprovalsIcon style={sideBarIconFontSize} />
                },
                {
                    url: '/history/page/0',
                    label: 'Transactions',
                    icon: <TransactionsIcon style={sideBarIconFontSize} />
                },

                {
                    url: '/users-list/page/0',
                    label: 'Employees',
                    icon: <EmployeesIcon style={sideBarIconFontSize} />
                },
                {
                    url: '/reconciliation',
                    label: 'Reconciliation',
                    icon: <ReconciliationIcon style={sideBarIconFontSize} />
                }
            ]
        },
        {
            label: 'Account',
            role: [UserRoles.Admin, UserRoles.SuperAdmin],
            items: [
                {
                    url: '/settings',
                    label: 'Settings',
                    icon: <SettingsIcon style={sideBarIconFontSize} />
                },
                {
                    url: '/subscription',
                    label: 'Subscription',
                    icon: <SubscriptionIcon style={sideBarIconFontSize} />
                }
            ]
        }
    ];

    const logoutOptions = [
        {
            role: [
                UserRoles.Admin,
                UserRoles.Manager,
                UserRoles.User,
                UserRoles.SuperAdmin
            ],
            items: [{ icon: <LogoutIcon />, url: 'logout', label: 'Logout' }]
        }
    ];

    const reportOption = {
        url: '/reports',
        label: 'Reports',
        icon: <DashboardIcon style={sideBarIconFontSize} />
    };

    const tipsOption = {
        url: '/tips-accounts/tips-settlement/page/0',
        label: 'InstaTips',
        icon: <TipsIcon style={sideBarIconFontSize} />
    };

    const instaSignOption = {
        url: '/insta-sign/page/0',
        label: 'InstaSign',
        icon: <InstaSignIcon style={sideBarIconFontSize} />
    };

    const menuTree: MenuTree = useMemo(() => {
        if (!getCompanyIdFromLocalStorage()) {
            return [getAdminLinks(), ...logoutOptions];
        }

        let updatedCommonLinks = [...commonLinks];
        if (currentCompany?.data.showReports) {
            updatedCommonLinks[0] = {
                ...updatedCommonLinks[0],
                items: [...updatedCommonLinks[0].items, reportOption]
            };
        }

        if (currentCompany?.data?.enableTips) {
            if (
                !updatedCommonLinks[0].items.some(
                    (item) => item.url === tipsOption.url
                )
            ) {
                updatedCommonLinks[0].items.splice(3, 0, tipsOption);
            }
        }

        // Always add InstaSign option after Tips (or at index 4 if Tips not present)
        const instaSignIndex = currentCompany?.data.enableTips ? 4 : 3;
        if (
            currentCompany?.data.enableInstaSign &&
            !updatedCommonLinks[0].items.some(
                (item) => item.url === instaSignOption.url
            )
        ) {
            updatedCommonLinks[0].items.splice(
                instaSignIndex,
                0,
                instaSignOption
            );
        }

        commonLinks = updatedCommonLinks;

        if (companies.length == 1) {
            return [...updatedCommonLinks, ...logoutOptions];
        }

        return [getAdminLinks(), ...updatedCommonLinks, ...logoutOptions];
    }, [companies, user, selectedCompanyName, currentCompany?.data]);

    return { menuTree } as const;
};
